import { useState } from 'react';
import { MoreHorizontal, Plus, Trash2, Users } from 'lucide-react';
import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  StatusPill,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { Section } from '@vnetwork/ui/layout';
import { useResellerCustomers, useRemoveResellerCustomer, type ResellerCustomerRow } from '@/api/resellers';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '@/pages/customers/pagination-summary';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatDate } from '@/lib/format';
import { AddResellerCustomerDialog } from './add-reseller-customer-dialog';

const PAGE_SIZE = 20;

function customerStatusTone(status: string) {
  switch (status) {
    case 'active':    return 'success' as const;
    case 'suspended': return 'danger' as const;
    case 'inactive':  return 'muted' as const;
    default:          return 'neutral' as const;
  }
}

function humanize(s: string) {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

/** Customers tab: DataTable of linked customers + Add Customer + Remove confirm. */
export function ResellerCustomersTab({ resellerId }: { resellerId: number }) {
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<number | null>(null);

  const { data, isLoading, isError, refetch } = useResellerCustomers(resellerId, {
    page,
    page_size: PAGE_SIZE,
  });
  const remove = useRemoveResellerCustomer(resellerId);

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const columns: ColumnDef<ResellerCustomerRow, unknown>[] = [
    {
      id: 'customer_id',
      header: () => <div style={{ textAlign: 'right' }}>ID</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {row.original.customer_id}
        </div>
      ),
    },
    {
      id: 'customer_name',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.customer_name}
        />
      ),
    },
    {
      id: 'customer_email',
      header: 'Email',
      cell: ({ row }) => row.original.customer_email || '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={customerStatusTone(row.original.status)}>
          {humanize(row.original.status)}
        </StatusPill>
      ),
    },
    {
      id: 'joined_at',
      header: 'Joined',
      cell: ({ row }) => formatDate(row.original.joined_at),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Row actions"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={15} />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              tone="danger"
              onSelect={() => setRemoveTarget(row.original.customer_id)}
            >
              <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <Section
      title="Linked Customers"
      actions={
        <Button
          variant="secondary"
          startIcon={<Plus size={15} />}
          onClick={() => setAddOpen(true)}
        >
          Add Customer
        </Button>
      }
    >
      {isError ? (
        <TabError title="Failed to load customers" onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={5} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          emptyState={
            <EmptyState
              icon={<Users size={24} />}
              title="No customers linked"
              description="Add a customer to this reseller."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={view.summary}
              />
            );
          })()}
        />
      )}

      <AddResellerCustomerDialog
        open={addOpen}
        resellerId={resellerId}
        onClose={() => setAddOpen(false)}
        onSuccess={() => {
          setAddOpen(false);
          refetch();
        }}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}
        title="Remove customer"
        description="Are you sure you want to remove this customer from the reseller?"
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (removeTarget !== null) {
            await remove.mutateAsync(removeTarget);
            setRemoveTarget(null);
          }
        }}
      />
    </Section>
  );
}
